import React, { useState, useEffect } from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { HelmetProvider } from 'react-helmet-async';
import axios from 'axios';
import { Toaster } from './components/ui/sonner';
import { toast } from 'sonner';
import { useTranslation } from './i18n';
import { ThemeProvider } from './contexts/ThemeContext';
import '@/App.css';

// Pages
import HomePage from './pages/HomePage';
import InventoryPage from './pages/InventoryPage';
import VehicleDetailPage from './pages/VehicleDetailPage';
import FavoritesPage from './pages/FavoritesPage';
import SavedSearches from './pages/SavedSearches';
import ContactPage from './pages/ContactPage';
import UserLogin from './pages/UserLogin';
import AdminLogin from './pages/AdminLogin';
import AdminRegister from './pages/AdminRegister';
import AdminDashboard from './pages/AdminDashboard';
import ComparePage from './pages/ComparePage';
import MyMessages from './pages/MyMessages';
import ForgotPassword from './pages/ForgotPassword';
import ResetPassword from './pages/ResetPassword';
import UserProfile from './pages/UserProfile';

// Components
import Navbar from './components/Navbar';
import CompareBar from './components/CompareBar';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Axios interceptor for auth
axios.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

function App() {
  // Public site is locked to EN/ES only — TR is admin-only
  const PUBLIC_LANGS = ['en', 'es'];
  const readPublicLang = () => {
    const saved = localStorage.getItem('lang');
    return PUBLIC_LANGS.includes(saved) ? saved : 'en';
  };
  const [user, setUser] = useState(null);
  const [authLoading, setAuthLoading] = useState(true);
  const [lang, setLang] = useState(readPublicLang);
  const [adminLang, setAdminLang] = useState(localStorage.getItem('adminLang') || 'en');
  const { t } = useTranslation(lang);

  useEffect(() => {
    // Check if user is logged in
    const token = localStorage.getItem('token');
    if (token) {
      checkAuth();
    } else {
      setAuthLoading(false);
    }
  }, []);

  const checkAuth = async () => {
    try {
      const response = await axios.get(`${API}/auth/me`);
      setUser(response.data);
      try { localStorage.setItem('user', JSON.stringify(response.data)); } catch (e) { /* ignore */ }
    } catch (error) {
      // Only remove token if it's a 401 (unauthorized)
      // Don't remove on network errors or other issues
      if (error.response && error.response.status === 401) {
        localStorage.removeItem('token');
        localStorage.removeItem('user');
        setUser(null);
      }
      // If other error, keep token and try again later
    } finally {
      setAuthLoading(false);
    }
  };

  const handleLogin = (userData) => {
    setUser(userData);
  };

  const handleLogout = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    setUser(null);
    toast.success('Logged out successfully');
  };

  const changeLanguage = (newLang) => {
    // Sanitize: public site never accepts 'tr'
    const safeLang = PUBLIC_LANGS.includes(newLang) ? newLang : 'en';
    setLang(safeLang);
    localStorage.setItem('lang', safeLang);
  };

  const changeAdminLang = (newLang) => {
    setAdminLang(newLang);
    localStorage.setItem('adminLang', newLang);
  };

  return (
    <HelmetProvider>
      <ThemeProvider>
        <div className="App">
          <BrowserRouter>
          <Navbar user={user} onLogout={handleLogout} lang={lang} onChangeLang={changeLanguage} />
          <Routes>
            <Route path="/" element={<HomePage lang={lang} />} />
            <Route path="/inventory" element={<InventoryPage lang={lang} />} />
            <Route path="/favorites" element={user ? <FavoritesPage lang={lang} /> : <Navigate to="/login" />} />
            <Route path="/saved-searches" element={user ? <SavedSearches lang={lang} /> : <Navigate to="/login" />} />
            <Route path="/compare" element={<ComparePage lang={lang} />} />
            <Route path="/my-messages" element={<MyMessages lang={lang} user={user} />} />
            <Route path="/contact" element={<ContactPage lang={lang} />} />
            <Route path="/login" element={<UserLogin onLogin={handleLogin} lang={lang} />} />
            <Route path="/forgot-password" element={<ForgotPassword lang={lang} />} />
            <Route path="/reset-password" element={<ResetPassword lang={lang} />} />
            <Route path="/profile" element={user ? <UserProfile user={user} onLogout={handleLogout} lang={lang} /> : <Navigate to="/login" />} />
            <Route path="/admin/login" element={<AdminLogin onLogin={handleLogin} lang={lang} />} />
            <Route path="/admin/register" element={<AdminRegister onLogin={handleLogin} lang={lang} />} />
            <Route 
              path="/admin/*" 
              element={
                authLoading ? (
                  <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
                    <div>Loading...</div>
                  </div>
                ) : user?.is_admin ? (
                  <AdminDashboard lang={adminLang} onLangChange={changeAdminLang} />
                ) : (
                  <Navigate to="/admin/login" />
                )
              } 
            />
            {/* Vehicle detail page - must be last as it's a catch-all for vehicle slugs */}
            <Route path="/vehicle/:slug" element={<VehicleDetailPage lang={lang} user={user} />} />
            <Route path="/:slug" element={<VehicleDetailPage lang={lang} user={user} />} />
          </Routes>
          <CompareBar lang={lang} />
          <Toaster position="top-center" richColors expand={true} />
        </BrowserRouter>
      </div>
      </ThemeProvider>
    </HelmetProvider>
  );
}

export default App;
